/* eslint-disable react/prop-types */
import React from 'react';
import { Form, Button, Select, Row, Col, Input, Switch } from 'antd';
import PropTypes from 'prop-types';
import { UserAccessContext } from 'context/userAccess';
import moment from 'moment';
import { NumberInput } from 'shared_components/pattern_input';
import { fieldLabels, formMode, buttonNames, fieldRules} from '../../settings';

import './fillup_form.css';

const { Option } = Select;

class FillupForm extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: false,
		}

		this.formRef = React.createRef();
	}

	componentDidMount() {
		const { ageBracket, moduleType } = this.props;
		const form = this.formRef.current;

		// For UPDATE mode only, populate form with existing data
		// For ADD mode, let the initialValue prop handle the default values
		if (moduleType === formMode.update) {
			const { ageBracketLabel, bracketFrom, bracketFromUnit, bracketTo, bracketToUnit, active } = ageBracket;

			form.setFieldsValue({ 
				ageBracketLabel,
				bracketFrom,
				bracketFromUnit,
				bracketTo,
				bracketToUnit,
				active: active === 1
			});
		}
	}

	componentDidUpdate(prevProps) {
		const { ageBracket, moduleType } = this.props;
		const form = this.formRef.current;

		// Reset form when switching modes (ADD to UPDATE or vice versa) or when ageBracketID changes
		if(moduleType !== prevProps.moduleType || ageBracket.ageBracketID !== prevProps.ageBracket.ageBracketID) {
			if (moduleType === formMode.add) {
				// For ADD mode, reset to initial values
				form.resetFields();
			} else {
				// For UPDATE mode, populate with the selected ageBracket data
				const { ageBracketLabel, bracketFrom, bracketFromUnit, bracketTo, bracketToUnit, active } = ageBracket;

				form.setFieldsValue({ 
					ageBracketLabel,
					bracketFrom,
					bracketFromUnit,
					bracketTo,
					bracketToUnit,
					active: active === 1
				});
			}
		}
	}

	resetForm= () => {
		const { resetFields } = this.formRef.current;

		resetFields();
	}

	onFormSubmit = (fieldValues) => {
		const { onSubmit } = this.props;
		this.setState({ isLoading: true }, async() => {
			await onSubmit({ ...fieldValues, active: fieldValues.active ? 1 : 0 });

			this.setState({ isLoading: false });
		});
	}

	// Private functions
	validateAge = (rule, value, callback) => {
		const { getFieldsValue } = this.formRef.current;
		const { bracketFrom, bracketTo, bracketFromUnit, bracketToUnit } = getFieldsValue();

		// Reference: https://momentjs.com/docs/#/manipulating/add/
		const fromDate = moment().add(bracketFrom, `${bracketFromUnit}`.toLowerCase());
		const toDate = moment().add(bracketTo, `${bracketToUnit}`.toLowerCase());


		if(!bracketFrom || !bracketTo || !bracketFromUnit || !bracketToUnit) {
			return callback();
		}

		// Reference: https://momentjs.com/docs/#/query/is-before/
		// Allow same age (e.g., 0-0 years), only reject if TO is strictly less than FROM
		if (moment(toDate).isBefore(fromDate)) {
			return callback('Invalid age range');
		}

		return callback();
	}

	render() {
		const { isLoading } = this.state;
		const { 
			onClose, 
			moduleType,
			// rangeClass
		} = this.props;

		return (
			<Form 
				ref={this.formRef}
				onFinish={this.onFormSubmit} 
				className="age-bracket-fillup-form"
				layout="vertical"
			>
				<section style={{ marginBottom: 50 }}>
					<section className="form-values">
						{
							moduleType === formMode.update ?
							(
								<Row align="middle">
									<Col span={24}>
										<Form.Item 
											label= "STATUS"
											name="active"
											valuePropName="checked"
											initialValue
										>
											<Switch />
										</Form.Item>
									</Col>
								</Row>
							) :
							
							(
								<Row align="middle">
									<Col span={24}>
										<Form.Item 
											label= "STATUS"
											name="active"
											valuePropName="checked"
											initialValue={true}
										>
											<Switch disabled={true}/>
										</Form.Item>
									</Col>
								</Row>
							)
						}
						<Row style={{ marginTop: 10 }}>
							<Col span={12}>
								<Form.Item 
									name="ageBracketLabel"
									label={fieldLabels.ageBracketRangeLabel}
								>
									<Input maxLength={254}/>
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={12}>
							<Col span={4}>
								<Form.Item
									name="bracketFrom"
									label={fieldLabels.ageBracketFrom}
									rules={fieldRules.ageBracketFrom}
									dependencies={['bracketFrom', 'bracketFromUnit', 'bracketTo', 'bracketToUnit']}
								>
									<NumberInput maxLength={3} />
								</Form.Item>
							</Col>
							<Col span={10}>
								<Form.Item
									name="bracketFromUnit"
									label={fieldLabels.ageBracketUnit}
									rules={fieldRules.ageBracketUnit}
									dependencies={['bracketFrom', 'bracketFromUnit', 'bracketTo', 'bracketToUnit']}
								>
									<Select>
										<Option value="DAYS">DAYS</Option>
										<Option value="WEEKS">WEEKS</Option>
										<Option value="MONTHS">MONTHS</Option>
										<Option value="YEARS">YEARS</Option>
									</Select>
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={12}>
							<Col span={4}>
								<Form.Item 
									name="bracketTo"
									label={fieldLabels.ageBracketTo}
									rules={[
										...fieldRules.ageBracketTo, 
										{ validator: this.validateAge }
									]}
									dependencies={['bracketFrom', 'bracketFromUnit', 'bracketTo', 'bracketToUnit']}
								>
									<NumberInput maxLength={3} />
								</Form.Item>
							</Col>
							<Col span={10}>
								<Form.Item 
									name="bracketToUnit"
									label={fieldLabels.ageBracketUnit}
									rules={[
										...fieldRules.ageBracketUnit,
										{ validator: this.validateAge }
									]}
									dependencies={['bracketFrom', 'bracketFromUnit', 'bracketTo', 'bracketToUnit']}
								>
									<Select>
										<Option value="DAYS">DAYS</Option>
										<Option value="WEEKS">WEEKS</Option>
										<Option value="MONTHS">MONTHS</Option>
										<Option value="YEARS">YEARS</Option>
									</Select>
								</Form.Item>
							</Col>
						</Row>
					</section>
				</section>
				<section className="drawerFooter">
					<div>
						<Button 
							shape="round" 
							style={{ margin: 10, width: 120 }}
							onClick={onClose}
						>
							{buttonNames.cancel}
						</Button>
						<UserAccessContext.Consumer>
									{value => value.userAccess.settings.create && (
						<Button 
							shape="round" 
							type="primary" 
							htmlType="submit"
							loading={isLoading}
							style={{ margin: 10, width: 120 }}
						>
							{(moduleType === formMode.add) ?  buttonNames.create : buttonNames.update}
						</Button>
						)}
						</UserAccessContext.Consumer>
					</div>
				</section>
			</Form>
		);
	}
}

FillupForm.propTypes = {
	moduleType: PropTypes.string.isRequired,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	ageBracket: PropTypes.shape({
		ageBracketID: PropTypes.number,
		from: PropTypes.string,
		to: PropTypes.string,
		unitFrom: PropTypes.string,
		unitTo: PropTypes.string
	}),
};

FillupForm.defaultProps = {
	ageBracket: {
		ageBracketID: null,
		from: null,
		to: null,
		unitFrom: null,
		unitTo: null
	}
}


export default FillupForm;